import { isAxiosError } from 'axios'
import toast from 'react-hot-toast'
import { generatePath } from 'react-router'
import { dayjs } from '@/dayjs'
import type {
	MARQUEE_RSVP_ENTRY_OPTIONS,
	MarqueeLocationStatus,
	MarqueeOperationId,
	MarqueeSegment,
	MarqueeTeamStatus
} from '@/features/marquee/types'
import { errorHandler } from '@/helpers/utils'
import { routes } from '@/routes'
import type {
	ApiError,
	MarqueeAssignableOperatorRole,
	MarqueeAttendee,
	MarqueeAttendeeStatus,
	MarqueeCaptureMode,
	MarqueeEventDetail,
	MarqueeEventFormat,
	MarqueeEventListItem,
	MarqueeEventState,
	MarqueeEventType,
	MarqueeExportPreview,
	MarqueeExportRow,
	MarqueeLocation,
	MarqueeManagedOperator,
	MarqueeOperator,
	MarqueeOperatorRole,
	MarqueeResolutionSourceStatus,
	MarqueeResolutionSourceUploadResult,
	MarqueeRsvpStatus,
	ValidationError
} from '@/sdk'
import i18next, { type TKey } from '@/translations/i18n'

// ─── Event labels ─────────────────────────────────────────────────────────────

/**
 * Display label for an event format — the Salesforce Activity Type picklist,
 * shown as the backend spells it: `format` is the one export cell we decide, so
 * the on-screen value is exactly what lands in Salesforce's Type column.
 *
 * Not to be confused with [[captureModeToLabelKey]] (how CTAs are captured) or
 * [[eventTypeToLabelKey]] (in-person vs virtual); neither leaves the tool.
 */
export const eventFormatToLabel = {
	Call: 'Call',
	Email: 'Email',
	Meeting: 'Meeting',
	SMS: 'SMS',
	Other: 'Other',
	Conference: 'Conference',
	'Market Visit': 'Market Visit'
} as const satisfies Record<MarqueeEventFormat, string>

/** i18n key for each capture mode's label. */
export const captureModeToLabelKey = {
	presentation: 'marquee:form_capture_mode_presentation',
	tune_up: 'marquee:form_capture_mode_tune_up'
} as const satisfies Record<MarqueeCaptureMode, TKey>

/** i18n key for each capture mode's one-line explanation, shown under its option. */
export const captureModeToHintKey = {
	presentation: 'marquee:form_capture_mode_presentation_hint',
	tune_up: 'marquee:form_capture_mode_tune_up_hint'
} as const satisfies Record<MarqueeCaptureMode, TKey>

/** i18n key for each event type label (in-person / virtual). */
export const eventTypeToLabelKey = {
	in_person: 'marquee:form_event_type_in_person',
	virtual: 'marquee:form_event_type_virtual'
} as const satisfies Record<MarqueeEventType, TKey>

/**
 * i18n key for an event's in-person/virtual label, `null` included: the field
 * is required on every read model, but events created before it existed carry
 * `null` and read as "not recorded" rather than silently as in-person.
 */
export const eventTypeLabelKey = (
	eventType: MarqueeEventType | null | undefined
): TKey =>
	eventType
		? eventTypeToLabelKey[eventType]
		: 'marquee:event_type_not_recorded'

/** i18n key for each lifecycle state's badge label. */
export const eventStateToLabelKey = {
	pre_work: 'marquee:status_badge_pre_work',
	locked: 'marquee:status_badge_locked',
	live: 'marquee:status_badge_live',
	closed: 'marquee:status_badge_closed'
} as const satisfies Record<MarqueeEventState, TKey>

/**
 * i18n key for the attendee data-source label. Open string domain (the backend
 * `data_source` is a free string), so unknown sources resolve to `undefined`.
 */
export const dataSourceToLabelKey: Record<string, TKey> = {
	salesforce: 'marquee:data_source_salesforce',
	csv_import: 'marquee:data_source_csv_import'
}

// ─── Full funnel (RSVP → event) ─────────────────────────────────────────────

/**
 * i18n key for each invite answer, as the import stored it.
 *
 * `invited` is an explicit answer ("invite sent, nothing came back"), which is
 * why it is not the same as `rsvp_status` being null (see [[hasRsvpData]]).
 * `no_response` is the closing verdict "invited and never answered"; the import
 * vocabulary (`Sent` / `Attending` / `Not Attending`) never produces it, so it
 * only appears once someone closes the responses out.
 */
export const rsvpStatusToLabelKey = {
	invited: 'marquee:rsvp_status_invited',
	rsvp_yes: 'marquee:rsvp_status_rsvp_yes',
	declined: 'marquee:rsvp_status_declined',
	no_response: 'marquee:rsvp_status_no_response'
} as const satisfies Record<MarqueeRsvpStatus, TKey>

/**
 * Did the invitee import carry RSVP data at all?
 *
 * A single row's `rsvp_status` being null means the import brought no RSVP fact
 * for **that** row (no column, or a value it could not read), worth flagging as
 * a row to fix. But a roster where *no* row carries one says nothing about the
 * rows: that is an import made without the RSVP column at all, and flagging every
 * row would blame the operator for a column they were never given. So the per-row
 * gap is only claimed when the roster proves RSVP data exists.
 *
 * The roster is never paginated, so this reads the whole list, not a page of it.
 */
export const hasRsvpData = (rows: MarqueeAttendee[]): boolean =>
	rows.some((row) => row.rsvp_status != null)

/** Rows whose invite answer is `status`. */
const countRsvp = (
	rows: MarqueeAttendee[],
	status: MarqueeRsvpStatus
): number => rows.filter((row) => row.rsvp_status === status).length

/**
 * Invite-answer tallies for the roster summary, or `null` when the import
 * carried no RSVP data (see [[hasRsvpData]]) and every tile would be claiming a
 * zero it cannot support.
 *
 * `awaiting` deliberately spans `invited` **and** `no_response`: both mean the
 * person has not answered, the first while answers are still open and the second
 * once they are closed. Counting only `no_response` would read 0 on every event
 * the import produced, since the CSV vocabulary cannot express it.
 */
export const rsvpTally = (
	rows: MarqueeAttendee[]
): { declined: number; awaiting: number } | null => {
	if (!hasRsvpData(rows)) {
		return null
	}
	return {
		declined: countRsvp(rows, 'declined'),
		awaiting: countRsvp(rows, 'invited') + countRsvp(rows, 'no_response')
	}
}

/**
 * How many rows the lock would close as "no response": the ones still reading
 * `invited`.
 *
 * The lock ends the RSVP window, and the backend writes `no_response` over every
 * `invited` row. That write is permanent: the lock is irreversible, and the RSVP
 * is editable in `pre_work` only (product decision, 2026-08-04), so there is no
 * way back short of re-importing the roster, which destroys every manual pin and
 * unresolvable mark on the event. Hence the number is stated before the operator
 * commits.
 *
 * Counts `invited` and not [[rsvpTally]]'s `awaiting`: the lock only rewrites that
 * lane, so a row already recorded as `no_response` is not something it changes.
 * Reads 0 on a roster imported without an RSVP column, which is exactly when there
 * is nothing to warn about.
 */
export const rsvpClosedByLockCount = (rows: MarqueeAttendee[]): number =>
	countRsvp(rows, 'invited')

/**
 * Rows that will silently fall out of the export: non-walk-ins carrying neither a
 * child nor a parent Salesforce account.
 *
 * This is the frontend's copy of the backend's M8 completeness gate, the one that
 * answers `completeness_warning.incomplete_rows` on the lock response. It is
 * recomputed here rather than read from that field because the field is populated
 * on the POST /lock response and nowhere else: one refresh and the number is gone,
 * on a transition that cannot be undone. Counting the roster instead keeps the
 * warning alive for as long as the problem is, and lets it fade row by row as the
 * accounts get filled in.
 *
 * Safe to recompute exactly: the attendee list is unpaginated, so `rows` is always
 * the whole roster and never a page of it.
 *
 * Both ids are required to be empty, which is what makes this narrower than "not
 * exportable". `tagged_at_parent` rows are deliberately out: they hold a parent
 * account, so they do reach Salesforce, just tagged one level up.
 *
 * Reading the two ids rather than `status` is deliberate, and the two agree. Status
 * is derived usable-account-first and first-match-wins (`ready` → `tagged_at_parent`
 * → `missing_account` → `unresolved` → `missing_host_id`), so a child id matches at
 * `ready` and a parent-only row at `tagged_at_parent`; the remaining three are
 * reachable only once both ids are empty. That leaves exactly `missing_host_id`,
 * `unresolved` and `missing_account` here — but they are the consequence, not the
 * test: those three say why the resolve failed, while the gate is about what the row
 * ended up holding. [[childAccountDisplay]] reads the same two fields in the same
 * order for the roster's account cell.
 *
 * Walk-ins are excluded to match the gate: they are expected to arrive without an
 * account and have their own pending-resolution lane on the export.
 */
export const hasNoSalesforceAccount = (attendee: MarqueeAttendee): boolean =>
	!(
		attendee.is_walk_in ||
		attendee.sf_child_account_id ||
		attendee.sf_parent_account_id
	)

/** How many rows [[hasNoSalesforceAccount]] holds — the count the lock warns about. */
export const incompleteAccountRows = (rows: MarqueeAttendee[]): number =>
	rows.filter(hasNoSalesforceAccount).length

/**
 * Did the operator state that this attendee arrived late?
 *
 * A stored assertion, not a measurement (OPSSTD-5121). The backend used to
 * compare the check-in instant against the event's actual start plus a grace
 * window; that instant records when the operator *reached* someone at the desk,
 * so a queue made punctual guests look late and an empty desk let genuinely
 * late ones through. The derivation and its grace window are gone, and the only
 * writer is the `is_late` flag on the check-in and walk-in bodies.
 *
 * `false` therefore means "not asserted late" and nothing more. It covers three
 * stored cases the wire cannot separate: the operator called them on time, no
 * one said anything, and rows checked in before the flag shipped (never
 * backfilled). So still never render "on time" off this field. Anything needing
 * that as its own answer needs a new one.
 *
 * The field is absent only where a response does not report attendance at all,
 * which is the four resolution fixes; the roster and the check-in, walk-in and
 * undo writes always carry it. Hence `=== true` rather than a truthiness test.
 */
export const isLateArrival = (attendee: MarqueeAttendee): boolean =>
	attendee.late_arrival === true

// ─── Check-in ───────────────────────────────────────────────────────────────

/**
 * Display status for the Day-of Check-in list, derived purely from
 * `checkin_status`: `arrived` (present, `logged_anyway` counts as arrived),
 * `no_show`, and `waiting` (not checked in yet). Host-ID resolution is an
 * export concern (`status`), never a check-in state.
 */
export type CheckInDisplayStatus = 'arrived' | 'no_show' | 'waiting'

/** Has the attendee been checked in (arrived or logged anyway)? */
export const isCheckedIn = (attendee: MarqueeAttendee): boolean =>
	attendee.checkin_status === 'arrived' ||
	attendee.checkin_status === 'logged_anyway'

export const deriveCheckInStatus = (
	attendee: MarqueeAttendee
): CheckInDisplayStatus => {
	if (attendee.checkin_status === 'no_show') {
		return 'no_show'
	}
	if (isCheckedIn(attendee)) {
		return 'arrived'
	}
	return 'waiting'
}

/**
 * What the roster's status badge shows: the check-in lane, except that a present
 * attendee the export cannot place gets its own state.
 *
 * Deliberately **not** a fourth [[CheckInDisplayStatus]]. The lane is the day-of
 * fact and the host ID is an export fact; keeping them apart is what lets the
 * header's check-in filter still find this row under "Arrived", which is what it
 * is. Only the badge merges them, because that is what the design draws.
 *
 * Read off the **current** host ID rather than `checkin_status === 'logged_anyway'`,
 * which records the same thing at the moment of check-in: capturing the ID later
 * has to turn the badge green, and a stored value never would.
 */
export type CheckInBadgeStatus = CheckInDisplayStatus | 'no_host_id'

export const deriveCheckInBadgeStatus = (
	attendee: MarqueeAttendee
): CheckInBadgeStatus => {
	const status = deriveCheckInStatus(attendee)
	return status === 'arrived' && !attendee.airbnb_host_id
		? 'no_host_id'
		: status
}

// ─── Roster header filters ──────────────────────────────────────────────────

/**
 * The RSVP facet's values, which are deliberately not the four stored ones.
 *
 * `awaiting` folds `invited` and `no_response` together for the same reason
 * [[rsvpTally]] does: both mean the person never answered, and which of the two
 * is on the row is a matter of whether the event has been locked, not of fact.
 * Kept apart, the operator would get one value matching every unanswered row
 * before the lock and the other matching every one after it, having filtered on
 * nothing they can see.
 */
export type RsvpFilterId = 'rsvp_yes' | 'declined' | 'awaiting'

export const rsvpFilterToLabelKey = {
	rsvp_yes: 'marquee:rsvp_status_rsvp_yes',
	declined: 'marquee:rsvp_status_declined',
	awaiting: 'marquee:rsvp_status_no_response'
} as const satisfies Record<RsvpFilterId, TKey>

export const checkInStatusToLabelKey = {
	arrived: 'marquee:checkin_status_arrived',
	no_show: 'marquee:checkin_status_no_show',
	waiting: 'marquee:checkin_status_waiting_host'
} as const satisfies Record<CheckInDisplayStatus, TKey>

/**
 * The RSVP facet's options in the design's reading order, shared by both roster
 * headers: the attendees tab and the check-in desk filter the same rows on the
 * same field, so a value offered on one and missing from the other would be a
 * difference with nothing behind it.
 */
export const rsvpFilterOptions = [
	{ id: 'rsvp_yes', label: rsvpFilterToLabelKey.rsvp_yes },
	{ id: 'declined', label: rsvpFilterToLabelKey.declined },
	{ id: 'awaiting', label: rsvpFilterToLabelKey.awaiting }
] as const satisfies readonly { id: RsvpFilterId; label: TKey }[]

/** Which facet value an invite answer falls under, `null` when there is none. */
const rsvpFilterId = (attendee: MarqueeAttendee): RsvpFilterId | null => {
	switch (attendee.rsvp_status) {
		case 'rsvp_yes':
			return 'rsvp_yes'
		case 'declined':
			return 'declined'
		case 'invited':
		case 'no_response':
			return 'awaiting'
		default:
			return null
	}
}

/**
 * Does the attendee pass the roster header's two facets?
 *
 * An empty facet is not a filter but "any value", so it keeps every row: that is
 * what makes the two independent and what the cleared state means.
 *
 * A row whose `rsvp_status` is null belongs to no facet value at all, so it drops
 * out as soon as the RSVP facet is narrowed. That is the honest answer rather
 * than a bug: the import carried no invite answer for that row (see
 * [[hasRsvpData]]), so it cannot be claimed to match one.
 */
export const attendeeMatchesFilters = (
	attendee: MarqueeAttendee,
	filters: {
		rsvp: readonly RsvpFilterId[]
		checkIn: readonly CheckInDisplayStatus[]
	}
): boolean => {
	const rsvp = rsvpFilterId(attendee)
	return (
		(filters.rsvp.length === 0 ||
			(rsvp !== null && filters.rsvp.includes(rsvp))) &&
		(filters.checkIn.length === 0 ||
			filters.checkIn.includes(deriveCheckInStatus(attendee)))
	)
}

// ─── Check-in summary tiles ─────────────────────────────────────────────────

/**
 * Turnout against the people who said yes, as a whole percentage, for the
 * caption under the check-in tile.
 *
 * A second ratio, not the tile's own fraction restated: the tile counts everyone
 * present out of everyone invited, this counts the same present total against the
 * `rsvp_yes` lane only. So it can read **over 100%**, and legitimately: walk-ins
 * and anyone whose invite answer never came back are present without ever having
 * confirmed. That is the interesting number on the day, so it is not clamped.
 *
 * `null` when nobody confirmed, which covers both the genuine zero and a roster
 * imported without an RSVP column at all (see [[hasRsvpData]]) — the caption has
 * no denominator either way, and a 0% would be a claim the data cannot support.
 */
export const checkedInRsvpShare = (rows: MarqueeAttendee[]): number | null => {
	const confirmed = countRsvp(rows, 'rsvp_yes')
	if (confirmed === 0) {
		return null
	}
	return Math.round((rows.filter(isCheckedIn).length / confirmed) * 100)
}

/**
 * Walk-ins split by how their Salesforce account stands, for the caption under
 * the walk-in tile.
 *
 * Same precedence as [[checkInRowActions]], so the caption and the row agree: an
 * operator's explicit unresolvable verdict wins over everything, then a host ID
 * means resolved, and the rest are still fixable. The three lanes are disjoint
 * and always sum to `total`.
 *
 * Deliberately counts every walk-in, checked in or not: a walk-in is created
 * already present, so the distinction the export cares about is the account, not
 * the arrival.
 */
export const walkInResolution = (
	rows: MarqueeAttendee[]
): {
	total: number
	resolved: number
	pending: number
	unresolvable: number
} => {
	const walkIns = rows.filter((row) => row.is_walk_in)
	const unresolvable = walkIns.filter((row) => row.is_unresolvable).length
	const resolved = walkIns.filter(
		(row) => !row.is_unresolvable && Boolean(row.airbnb_host_id)
	).length
	return {
		total: walkIns.length,
		resolved,
		pending: walkIns.length - unresolvable - resolved,
		unresolvable
	}
}

/**
 * How much hand reconciliation the roster is carrying right now: the work a
 * re-import puts at risk.
 *
 * Stated before the operator commits, because what a re-import will actually
 * keep is only knowable after the file has been matched against the roster, and
 * the server has not seen the file when the dialog opens. So the confirmation
 * quantifies what is on the table, and the carry report (see the import result)
 * is the after-the-fact truth.
 *
 * The three are counted independently and a row may be in more than one: a
 * hand-added walk-in can also carry a manual pin. Summing them would overstate.
 */
export const manualWorkTally = (
	rows: MarqueeAttendee[]
): { pins: number; marks: number; handAdded: number } => {
	const pins = rows.filter((row) => row.host_id_source === 'manual').length
	const marks = rows.filter((row) => row.is_unresolvable).length
	const handAdded = rows.filter((row) => row.origin === 'manual').length
	return { pins, marks, handAdded }
}

/**
 * Every secondary/contextual action the row menu (mobile sheet) or the desktop
 * action column can offer. IDs are kept distinct even when two share a backend
 * call (e.g. `undo` / `undo_no_show` both POST check-in/undo) so each keeps
 * its own label and tone.
 */
export type CheckInActionId =
	| 'check_in'
	| 'no_show'
	| 'undo'
	| 'undo_no_show'
	| 'check_in_late'
	| 'resolve_id_capture'
	| 'restore_resolution'
	| 'mark_unresolvable'

export interface CheckInAction {
	id: CheckInActionId
	tone: 'default' | 'danger' | 'ok'
}

/**
 * Which lifecycle gate an action answers to. The two are not the same window:
 *
 * - `checkin`: the day-of writes (arrive / no-show / undo). `live` only, the
 *   desk is open exactly while the event is running.
 * - `resolution`: the roster fix actions (mark unresolvable / restore). Every
 *   state **but `locked`**: they answer in `pre_work`, `live` and `closed` alike,
 *   so a row can be corrected while the event is in the room instead of waiting
 *   for it to close.
 *
 * `locked` is the one frozen state, and it is still a 409 `invalid-state`.
 */
export type CheckInActionScope = 'checkin' | 'resolution'

export const checkInActionScope = {
	check_in: 'checkin',
	no_show: 'checkin',
	undo: 'checkin',
	undo_no_show: 'checkin',
	check_in_late: 'checkin',
	resolve_id_capture: 'resolution',
	restore_resolution: 'resolution',
	mark_unresolvable: 'resolution'
} as const satisfies Record<CheckInActionId, CheckInActionScope>

/** Which of the two windows are open for the event being rendered. */
export type CheckInActionGates = Record<CheckInActionScope, boolean>

/**
 * The gates for an event's current state. Both are closed while the event (or
 * its query) is still unknown, so nothing is offered before we know what it is.
 */
export const checkInActionGates = (
	state: MarqueeEventState | undefined
): CheckInActionGates => ({
	checkin: state === 'live',
	resolution: state !== undefined && state !== 'locked'
})

/**
 * Drops the actions whose window is closed, keeping [[checkInRowActions]]'s
 * matrix a pure function of the row.
 */
export const allowedCheckInActions = <T extends CheckInAction>(
	actions: T[],
	gates: CheckInActionGates
): T[] => actions.filter((action) => gates[checkInActionScope[action.id]])

/** i18n key for each action's label (shared desktop kebab + mobile sheet). */
export const checkInActionToLabelKey = {
	check_in: 'marquee:checkin_action_check_in',
	no_show: 'marquee:checkin_action_no_show',
	undo: 'marquee:checkin_action_undo',
	undo_no_show: 'marquee:checkin_action_undo_no_show',
	check_in_late: 'marquee:checkin_action_check_in_late',
	resolve_id_capture: 'marquee:checkin_action_resolve_id_capture',
	restore_resolution: 'marquee:export_restore_resolution',
	mark_unresolvable: 'marquee:export_mark_unresolvable'
} as const satisfies Record<CheckInActionId, TKey>

/** Mobile checkbox behaviour for the row's primary (check-in) action. */
export type CheckboxMode = 'check_in' | 'undo' | 'disabled'

export interface CheckInRowActions {
	/** What the mobile row's tick does. */
	checkbox: CheckboxMode
	/**
	 * The row's full action list in the design's order, primary included. The
	 * mobile sheet drops whatever the tick already does (see
	 * [[secondaryCheckInActions]]), the desktop kebab renders it as-is beside the
	 * one action the row also draws inline (see [[checkInRowControls]]).
	 */
	menu: CheckInAction[]
	/**
	 * Candidates for the desktop row's single inline button, most wanted first:
	 * a preference order, not a set. [[checkInRowControls]] draws the first
	 * one the event's state allows, which is how a row can promote a check-in
	 * write while the desk is open and a resolution fix once it has shut.
	 */
	inline: CheckInActionId[]
}

/**
 * What a row offers on each of the three surfaces that render it, per the
 * action matrix (`marquee_kebab_menu_states.html`). Branches are ordered by
 * precedence, so each attendee matches exactly one.
 *
 * This is the row's side of the decision only. Whether the event's state allows
 * an action is [[allowedCheckInActions]], applied by the caller: the two windows
 * differ (see [[checkInActionScope]]), so a closed event still offers the
 * resolution fixes while offering no check-in write.
 */
export const checkInRowActions = (
	attendee: MarqueeAttendee
): CheckInRowActions => {
	// An operator's explicit "unresolvable" verdict wins over everything else:
	// the row is out of the export, so the desk has nothing else to do with it.
	// Walking the mark back is offered here all the same, because the export tab
	// (where the mark is made) does not open until the event closes, and a row
	// written off at 10:30 would otherwise be untouchable for the rest of the day.
	if (attendee.is_unresolvable) {
		return {
			checkbox: 'disabled',
			menu: [{ id: 'restore_resolution', tone: 'default' }],
			inline: ['restore_resolution']
		}
	}

	if (attendee.checkin_status === 'no_show') {
		return {
			checkbox: 'disabled',
			menu: [
				{ id: 'undo_no_show', tone: 'default' },
				// Not in the design, kept on purpose: without it a no-show who
				// then walks in costs two steps (undo, then check-in).
				{ id: 'check_in_late', tone: 'ok' }
			],
			// The arrival, not the retraction: a no-show touched again has almost
			// always just walked in.
			inline: ['check_in_late']
		}
	}

	// Not checked in yet: a plain invitee awaiting arrival. Both arrival paths
	// sit here side by side, because a guest who simply turns up late is the
	// common case at a summit, not a correction to a no-show. Offering only the
	// on-time check-in made the operator mark the person a no-show first and
	// then walk it back: two writes, and a visibly wrong state in between, in
	// front of the person who had just arrived.
	if (!isCheckedIn(attendee)) {
		return {
			checkbox: 'check_in',
			menu: [
				{ id: 'check_in', tone: 'default' },
				{ id: 'check_in_late', tone: 'default' },
				{ id: 'no_show', tone: 'danger' }
			],
			// No-show is destructive and late is a qualifier on the same call, so
			// both stay a deliberate trip to the kebab.
			inline: ['check_in']
		}
	}

	// Present with no host ID: the row the export cannot place. The desk is where
	// the ID is obtainable at all, since the person is standing there, so both
	// routes out are offered: capture the ID, or write the row off. The write-off
	// is walked back by [[restore_resolution]] on the branch above, which is what
	// keeps it from being a one-way door mid-event.
	if (!attendee.airbnb_host_id) {
		return {
			checkbox: 'undo',
			menu: [
				{ id: 'undo', tone: 'default' },
				{ id: 'resolve_id_capture', tone: 'default' },
				{ id: 'mark_unresolvable', tone: 'danger' }
			],
			// Undo while the desk is open, the ID capture once it has shut: the
			// windows never overlap, so the row keeps a button either way.
			inline: ['undo', 'resolve_id_capture']
		}
	}

	// Checked in and clean (invitee, or walk-in whose host ID resolved): the
	// tick undoes, and the menu offers the same plus the detail link.
	return {
		checkbox: 'undo',
		menu: [{ id: 'undo', tone: 'default' }],
		inline: ['undo']
	}
}

/**
 * What the desktop row's action column draws: the button it promotes inline, and
 * the kebab's menu.
 *
 * `inline` is the first allowed candidate, or `null` when the row has nothing
 * open — the column then shows the kebab alone rather than a disabled button.
 * The menu is what is left over: an action the row already draws as a button is
 * dropped from it, so the kebab never repeats what is one click away beside it.
 * That is the same trim the mobile sheet makes against the card's tick (see
 * [[secondaryCheckInActions]]), applied to the surface that promotes a button
 * instead.
 */
export const checkInRowControls = (
	attendee: MarqueeAttendee,
	gates: CheckInActionGates
): { inline: CheckInAction | null; menu: CheckInAction[] } => {
	const rowActions = checkInRowActions(attendee)
	// Walked in `inline`'s order (the preference), not the menu's (the design's
	// reading order).
	const candidates = rowActions.inline.flatMap((id) =>
		rowActions.menu.filter((action) => action.id === id)
	)
	const [inline = null] = allowedCheckInActions(candidates, gates)
	const menu = allowedCheckInActions(rowActions.menu, gates).filter(
		(action) => action.id !== inline?.id
	)

	return { inline, menu }
}

/**
 * The mobile sheet's list: the row's menu minus whatever the tick already does.
 *
 * Unlike the desktop kebab, which lists everything the row can do (see
 * [[checkInRowControls]]), the sheet is opened from a card whose tick is already
 * the primary check-in/undo, sitting right beside it. Repeating that action a
 * finger's width away is what the trim avoids.
 */
export const secondaryCheckInActions = (
	rowActions: CheckInRowActions
): CheckInAction[] =>
	rowActions.menu.filter(
		(action) => action.id !== primaryActionForCheckbox[rowActions.checkbox]
	)

const primaryActionForCheckbox = {
	check_in: 'check_in',
	undo: 'undo',
	disabled: undefined
} as const satisfies Record<CheckboxMode, CheckInActionId | undefined>

// ─── Per-attendee CTA capture ────────────────────────────────────────────────

/**
 * Why per-attendee CTA capture is unavailable for this row, or `null` when it is
 * allowed. Mirrors the backend's own gates, in its precedence order, so the UI
 * explains the refusal instead of letting the operator discover it as a 409:
 *
 * - `presentation` — the event captures one blanket selection for the room;
 *   per-attendee editing exists only for `tune_up`.
 * - `not_started` — `pre_work` / `locked`. Note `closed` is **not** blocked on
 *   its own: editing stays open until the attendee is exported, which is how
 *   a missed capture gets fixed before the run.
 * - `not_checked_in` — capture records a conversation that happened, so it
 *   needs a *present* check-in: never checked in, `no_show` and an undone
 *   check-in are all refused. Walk-ins qualify, being created already present.
 * - `exported` — the row was carried by an export run (OPSSTD-5277's
 *   `MarqueeAttendee.exported_at`, non-null). The write-side 409
 *   `export-locked` remains the source of truth; this only lets the gate be
 *   pre-checked instead of discovered on save, same as the other three.
 */
export type CtaCaptureBlock =
	| 'presentation'
	| 'not_started'
	| 'not_checked_in'
	| 'exported'

export const ctaCaptureBlock = (
	event: Pick<MarqueeEventDetail, 'capture_mode' | 'state'> | undefined,
	attendee: MarqueeAttendee | undefined
): CtaCaptureBlock | null => {
	if (!(event && attendee)) {
		return 'not_started'
	}
	if (event.capture_mode !== 'tune_up') {
		return 'presentation'
	}
	if (!(event.state === 'live' || event.state === 'closed')) {
		return 'not_started'
	}
	if (!isCheckedIn(attendee)) {
		return 'not_checked_in'
	}
	if (attendee.exported_at != null) {
		return 'exported'
	}
	return null
}

/** i18n key for the banner title of each blocked state. */
export const ctaCaptureBlockToTitleKey = {
	presentation: 'marquee:attendee_detail_cta_blanket_title',
	not_started: 'marquee:attendee_detail_preview_title',
	not_checked_in: 'marquee:attendee_detail_cta_absent_title',
	exported: 'marquee:attendee_detail_cta_exported_title'
} as const satisfies Record<CtaCaptureBlock, TKey>

/** i18n key for the banner body of each blocked state. */
export const ctaCaptureBlockToLabelKey = {
	presentation: 'marquee:attendee_detail_cta_blanket_body',
	not_started: 'marquee:attendee_detail_preview_body',
	not_checked_in: 'marquee:attendee_detail_cta_absent_body',
	exported: 'marquee:attendee_detail_cta_exported_body'
} as const satisfies Record<CtaCaptureBlock, TKey>

/**
 * Has this attendee's CTA set been tailored 1:1, as opposed to still being the
 * event's blanket snapshot?
 *
 * `ctas_customized_at` is the only provenance signal: a cleared set is empty
 * AND tailored, so emptiness says nothing. (`ctas_customized_by` reads `null`
 * everywhere until the auth swap lands, so nothing may depend on it.)
 */
export const isCtaSetTailored = (attendee: MarqueeAttendee): boolean =>
	attendee.ctas_customized_at != null

/**
 * i18n key for each rejection the CTA write can answer with **except**
 * `invalid-state`: that code is shared with seven other writes and is worded by
 * [[invalidStateMessage]], which the panel consults first. The three below are
 * this endpoint's own and say what no action name could.
 */
const ctaWriteErrorToLabelKey = {
	'invalid-cta-code': 'marquee:error_cta_invalid_code',
	'export-locked': 'marquee:error_cta_export_locked',
	'attendee-not-found': 'marquee:error_cta_attendee_not_found'
} as const satisfies Record<string, TKey>

const isCtaWriteErrorCode = (
	code: string
): code is keyof typeof ctaWriteErrorToLabelKey =>
	code in ctaWriteErrorToLabelKey

/**
 * i18n key for a failed CTA write, or `undefined` when the failure is not one
 * the endpoint documents (leave those to the generic handler).
 */
export const attendeeCtaErrorKey = (error: unknown): TKey | undefined => {
	const body = errorBody(error)
	if (!(isApiError(body) && isCtaWriteErrorCode(body.code))) {
		return undefined
	}
	return ctaWriteErrorToLabelKey[body.code]
}

/**
 * Display label for a host segment. Brand terms (ProHost / Core), so they are
 * static like [[eventFormatToLabel]] rather than i18n keys.
 */
/**
 * i18n key for the roster's RSVP cell, `null` included.
 *
 * `null` is its own answer ("No RSVP"), not a blank and not `invited`: the two
 * mean different things and only one of them is a fact the import carried. The
 * three answers an operator can enter reuse the short wording of the add form,
 * since the cell doubles as the picker's current value.
 */
export const rsvpCellToLabelKey = (
	status: MarqueeRsvpStatus | null | undefined
): TKey => {
	if (!status) {
		return 'marquee:rsvp_cell_none'
	}
	return status === 'no_response'
		? 'marquee:rsvp_status_no_response'
		: rsvpEntryToLabelKey[status]
}

/**
 * Invite answers as the manual-add form words them: shorter than the roster's
 * own labels ([[rsvpStatusToLabelKey]]), which read as verdicts on a row that
 * already exists rather than as choices in a picker.
 */
export const rsvpEntryToLabelKey = {
	invited: 'marquee:rsvp_entry_invited',
	rsvp_yes: 'marquee:rsvp_entry_rsvp_yes',
	declined: 'marquee:rsvp_entry_declined'
} as const satisfies Record<(typeof MARQUEE_RSVP_ENTRY_OPTIONS)[number], TKey>

const segmentToLabel = {
	prohost: 'ProHost',
	core: 'Core'
} as const satisfies Record<MarqueeSegment, string>

const isKnownSegment = (segment: string): segment is MarqueeSegment =>
	segment in segmentToLabel

/**
 * Maps a host segment to its display label, `-` when absent.
 *
 * The API types `segment` as a free string (the invitee import stores whatever
 * the CSV carried), so an unrecognized value is shown as-is rather than
 * swallowed.
 */
export const segmentLabel = (segment?: string | null) => {
	if (!segment) {
		return '-'
	}
	return isKnownSegment(segment) ? segmentToLabel[segment] : segment
}

/**
 * Derives the child-account cell: the child SF account id when present,
 * otherwise the parent account id flagged as tagged-at-parent, else nothing.
 */
export const childAccountDisplay = (attendee: MarqueeAttendee) => {
	if (attendee.sf_child_account_id) {
		return { value: attendee.sf_child_account_id, isParent: false }
	}
	if (attendee.sf_parent_account_id) {
		return { value: attendee.sf_parent_account_id, isParent: true }
	}
	return { value: null, isParent: false }
}

// ─── Operator permissions ─────────────────────────────────────────────────────

/**
 * The operator's role, as the user menu words it.
 *
 * Display only. Nothing branches on the role: what an operator may do is
 * `permitted`, read by [[isMarketManagerAllowed]]. This exists so the header can
 * say who you are signed in as, and it is deliberately not the same vocabulary
 * as `auth:roles.*`, which words Luminate reviewer roles.
 */
export const operatorRoleToLabelKey = {
	creator: 'marquee:role_creator',
	facilitator: 'marquee:role_facilitator',
	admin: 'marquee:role_admin'
} as const satisfies Record<MarqueeOperatorRole, TKey>

/**
 * One-line explanation under each role's option in the Team form. Keyed on the
 * assignable roles only: `admin` is never offered.
 */
export const operatorRoleToHintKey = {
	creator: 'marquee:team_role_creator_hint',
	facilitator: 'marquee:team_role_facilitator_hint'
} as const satisfies Record<MarqueeAssignableOperatorRole, TKey>

/**
 * Is this market manager allowed to call this operation, ever?
 *
 * The whole client-side reading of the role partition (FR51), in one place. A
 * **creator** sets events up and closes them out, a **facilitator** works the
 * door, and a creator satisfies everything a facilitator may do.
 *
 * Not to be confused with `listMarketManagers`, which is the list of managers
 * assignable to an event (the chips on the form). That is a property of the
 * event; this is who is at the keyboard.
 *
 * Two answers, from one argument:
 *
 * - **an operator**: the operationId is in `permitted` or it is not, and that
 *   is final. Never read `role` instead: a per-role list of buttons in the
 *   client is a second copy of the backend's map, and copies drift.
 * - **`undefined`**: no answer, so not allowed. In practice unreachable inside
 *   the section, since `MarqueeSessionRoute` resolves the operator before any
 *   screen mounts and refuses entry when it cannot.
 *
 * Answers about the role only, never about the event. `true` means this manager
 * is allowed the verb at all, not that the event's current state accepts it
 * right now: that is a separate check on the state (`checkInActionGates`,
 * `invalidStateMessage`), and the two are combined at the call site into the
 * `show...` flag that actually renders a control.
 */
export const isMarketManagerAllowed = (
	operator: MarqueeOperator | undefined,
	operation: MarqueeOperationId
): boolean => operator?.permitted.includes(operation) ?? false

// ─── Navigation ───────────────────────────────────────────────────────────────

/**
 * Attendee-detail route for a row, or `undefined` when the page is not reachable
 * (so the name stays non-clickable). Shared by the attendees, check-in and export
 * tables so a name opens the same `/attendees/:attendeeId` page from anywhere.
 *
 * The page exists for a **tune-up only**: per-attendee CTA capture is what it is
 * for, and that is refused on a presentation, whose attendees all carry the same
 * blanket set. The route guards itself too, for anyone arriving by URL.
 */
export const attendeeDetailPath = (
	eventId: string | undefined,
	attendeeId: string | undefined,
	captureMode: MarqueeCaptureMode | undefined
): string | undefined =>
	eventId && attendeeId && captureMode === 'tune_up'
		? generatePath(routes.MARQUEE_EVENT_ATTENDEE_DETAIL.handle.fullPath, {
				id: eventId,
				attendeeId
			})
		: undefined

export const openExternalReport = (hashtag: string): string =>
	`https://marquee.sandcastle.musta.ch/?event=${hashtag.replace('#', '')}&window=60`

// ─── Write errors ─────────────────────────────────────────────────────────────

/** Parsed body of a failed SDK call; `undefined` when the failure wasn't HTTP. */
const errorBody = (error: unknown): unknown =>
	isAxiosError(error) ? error.response?.data : undefined

const isValidationError = (body: unknown): body is ValidationError =>
	typeof body === 'object' &&
	body !== null &&
	'code' in body &&
	body.code === 'validation-error' &&
	'errors' in body &&
	typeof body.errors === 'object' &&
	body.errors !== null

const isApiError = (body: unknown): body is ApiError =>
	typeof body === 'object' &&
	body !== null &&
	'code' in body &&
	typeof body.code === 'string' &&
	'message' in body &&
	typeof body.message === 'string'

// ─── Wrong-state refusals ─────────────────────────────────────────────────────

/**
 * The writes that answer the shared 409 `invalid-state`.
 *
 * The code is deliberately uniform across the domain — it means one thing, "the
 * event is in the wrong lifecycle state", and that uniformity is what lets this
 * client branch on it safely. What it cannot carry is *which* action was
 * refused: the backend has no way to know the operator was resolving a row
 * rather than capturing CTAs, and its own sentence says only that the action
 * conflicts with the state. The client always knows — it dispatched the call —
 * so the specific sentence is written here, keyed on the action.
 *
 * Operations that carry a code of their own (`event-not-in-pre-work` on import,
 * `event-not-live` on close) are deliberately absent: they keep their own
 * message, which already names the state that would work.
 */
export type MarqueeInvalidStateAction =
	| 'assign_host'
	| 'mark_unresolvable'
	| 'restore_resolution'
	| 'resolve_event'
	| 'capture_ctas'
	| 'set_rsvp'
	| 'change_blanket_cta'

/**
 * The lifecycle window each action writes in — the same matrix the endpoints
 * document, kept beside the messages so the two cannot drift:
 *
 * - the three roster fix actions: every state but `locked`
 * - `resolve_event`: the same window as the fixes it bulk-applies
 * - `set_rsvp`: `pre_work` only — the lock closes the invite window
 * - `capture_ctas`: `live`, and `closed` while the row is still unexported
 * - `change_blanket_cta`: `live` only
 */
const invalidStateActionWindow = {
	assign_host: ['pre_work', 'live', 'closed'],
	mark_unresolvable: ['pre_work', 'live', 'closed'],
	restore_resolution: ['pre_work', 'live', 'closed'],
	resolve_event: ['pre_work', 'live', 'closed'],
	capture_ctas: ['live', 'closed'],
	set_rsvp: ['pre_work'],
	change_blanket_cta: ['live']
} as const satisfies Record<MarqueeInvalidStateAction, MarqueeEventState[]>

/**
 * What each refusal says: the action that was refused, and what would make it
 * work.
 *
 * `capture_ctas` is the one whose refusal is genuinely ambiguous — the endpoint
 * answers the same code for a wrong event state, for an attendee who never
 * checked in, and for a `presentation` event. On a live tune-up the state is
 * demonstrably fine, so the state is not the cause and the message must not
 * imply it is: [[invalidStateMessage]] withholds the state sentence there and
 * lists every condition instead, leaving the operator the one that applies.
 */
const invalidStateActionToLabelKey = {
	assign_host: 'marquee:error_invalid_state_assign_host',
	mark_unresolvable: 'marquee:error_invalid_state_mark_unresolvable',
	restore_resolution: 'marquee:error_invalid_state_restore_resolution',
	resolve_event: 'marquee:error_invalid_state_resolve_event',
	capture_ctas: 'marquee:error_invalid_state_capture_ctas',
	set_rsvp: 'marquee:error_invalid_state_set_rsvp',
	change_blanket_cta: 'marquee:error_invalid_state_change_blanket_cta'
} as const satisfies Record<MarqueeInvalidStateAction, TKey>

/**
 * The same seven refusals, worded for when the event's state is known: each
 * names the state the event is actually in before saying what would work. Kept
 * as a parallel map rather than a prefix glued onto the sentence above, so a
 * translator controls the whole sentence in both cases.
 */
const invalidStateActionToStateLabelKey = {
	assign_host: 'marquee:error_invalid_state_assign_host_in',
	mark_unresolvable: 'marquee:error_invalid_state_mark_unresolvable_in',
	restore_resolution: 'marquee:error_invalid_state_restore_resolution_in',
	resolve_event: 'marquee:error_invalid_state_resolve_event_in',
	capture_ctas: 'marquee:error_invalid_state_capture_ctas_in',
	set_rsvp: 'marquee:error_invalid_state_set_rsvp_in',
	change_blanket_cta: 'marquee:error_invalid_state_change_blanket_cta_in'
} as const satisfies Record<MarqueeInvalidStateAction, TKey>

/** Did this failure come back as the shared 409 `invalid-state`? */
export const isInvalidStateError = (error: unknown): boolean => {
	const body = errorBody(error)
	return isApiError(body) && body.code === 'invalid-state'
}

/**
 * What to show for a refused write, or `undefined` when the failure is not the
 * shared `invalid-state` — those belong to the generic handler, or to the codes
 * that carry their own sentence.
 *
 * Returns keys, never a joined or half-translated string: the sentence key, and
 * — when the state is named — the key for the state's own label, which the
 * caller translates into the `{{state}}` slot. Two keys rather than one
 * interpolated string keeps every word of both sentences in the locale file.
 *
 * `eventState` only decides which of the two sentences an action gets. When it
 * is known and the action's window is genuinely shut, the message names the
 * state the event is in. When it is unknown, or when it disagrees with the
 * refusal because the event moved under the operator (a second tab closed it),
 * the state-less sentence stands — a stale state named confidently is worse
 * than one not named at all.
 */
export const invalidStateMessage = ({
	error,
	action,
	eventState
}: {
	error: unknown
	action: MarqueeInvalidStateAction
	eventState?: MarqueeEventState
}): { key: TKey; stateKey?: TKey } | undefined => {
	if (!isInvalidStateError(error)) {
		return undefined
	}

	const windowStates: readonly MarqueeEventState[] =
		invalidStateActionWindow[action]

	return eventState && !windowStates.includes(eventState)
		? {
				key: invalidStateActionToStateLabelKey[action],
				stateKey: eventStateToLabelKey[eventState]
			}
		: { key: invalidStateActionToLabelKey[action] }
}

/**
 * The CTA rejection, as a message to show on the CTA field.
 *
 * A `presentation` event must always keep at least one blanket CTA — it is what
 * every check-in snapshots — so create, update and cta-change all answer **422
 * `validation-error`** with the detail under `errors.ctas` when the selection
 * would end up empty. It is a validation error, not a 409: the state is fine,
 * the payload isn't. Returns `undefined` for any other failure.
 */
export const ctaValidationMessage = (error: unknown): string | undefined => {
	const body = errorBody(error)
	return isValidationError(body) ? body.errors.ctas?.[0] : undefined
}

/**
 * Did a manual attendee add bounce off an email already on the roster (409
 * `attendee-email-on-roster`)?
 *
 * The import keeps one row per email and the manual add holds that invariant,
 * matching case-insensitively on the trimmed address. The operator fixes it by
 * changing what they typed, so it belongs on the email field rather than in a
 * toast. The lifecycle check runs first server-side, so a `pre_work` failure is
 * the only way to see this code.
 */
export const isAttendeeEmailOnRoster = (error: unknown): boolean => {
	const body = errorBody(error)
	return isApiError(body) && body.code === 'attendee-email-on-roster'
}

// ─── Download ─────────────────────────────────────────────────────────────────

/**
 * Triggers a browser download for a CSV the API returned as text.
 *
 * The CSV endpoints are typed (and fetched) as `text/csv`, so the payload
 * arrives as a string rather than a Blob; the backend emits no BOM, so nothing
 * is lost in decoding. Wrapping it here keeps the encoding declaration in one
 * place: if Excel ever needs a BOM for non-ASCII host names, prepend `﻿`
 * to the Blob parts below.
 */
export const saveCsv = (filename: string, csv: string) => {
	saveBlob(filename, new Blob([csv], { type: 'text/csv;charset=utf-8' }))
}

/** Triggers a browser download for a Blob without losing the auth header. */
export const saveBlob = (filename: string, blob: Blob) => {
	const url = URL.createObjectURL(blob)
	const anchor = document.createElement('a')
	anchor.href = url
	anchor.download = filename
	document.body.appendChild(anchor)
	anchor.click()
	anchor.remove()
	URL.revokeObjectURL(url)
}

// ─── Time ───────────────────────────────────────────────────────────────────
/**
 * Composes a naive local datetime from an event `date` ("2026-07-15") and a
 * start time that is either a bare clock time ("16:00", the current API shape)
 * or already a full datetime ("2026-07-09T18:00:00"). Falls back to the time
 * as-is when no date is available, so callers still degrade gracefully.
 */
const composeLocalDateTime = (
	startTime: string,
	date?: string | null
): string => {
	if (startTime.includes('T')) {
		return startTime
	}
	if (!date) {
		return startTime
	}
	// Pad a bare "HH:mm" to "HH:mm:00" so it parses as a full local datetime.
	const withSeconds = startTime.length === 5 ? `${startTime}:00` : startTime
	return `${date}T${withSeconds}`
}

/**
 * Resolves a naive local start datetime + IANA timezone into an absolute instant
 * (ISO). The IANA name (e.g. "America/New_York") carries the DST rules, so the
 * offset is computed automatically, no timezone map.
 *
 * `("16:00", "America/New_York", "2026-07-15")` → `"2026-07-15T20:00:00.000Z"`.
 *
 * Returns `null` when either input is missing/invalid, callers then fall back
 * to the event `state`.
 */
export const resolveStartInstant = (
	startTime: string | undefined | null,
	timezone: string | undefined | null,
	date?: string | null
): string | null => {
	if (!(startTime && timezone)) {
		return null
	}
	const instant = dayjs.tz(composeLocalDateTime(startTime, date), timezone)
	return instant.isValid() ? instant.toISOString() : null
}

/**
 * Short timezone abbreviation ("EDT"/"EST") for an instant, in the given IANA
 * zone. Not a formatter: it labels a time the caller has already formatted, and
 * only a few places want that label: the status pill, the start-time meta line
 * and the attendee-import stamp. Everywhere else the zone is stated once in the
 * page header rather than repeated on every line.
 */
export const zoneAbbr = (
	iso: string | undefined | null,
	timezone: string | undefined | null
): string => {
	if (!(iso && timezone)) {
		return ''
	}
	const instant = dayjs(iso)
	// Guard: Intl.formatToParts throws "Invalid time value" on an invalid Date.
	if (!instant.isValid()) {
		return ''
	}
	const part = new Intl.DateTimeFormat('en-US', {
		timeZone: timezone,
		timeZoneName: 'short'
	})
		.formatToParts(instant.toDate())
		.find((p) => p.type === 'timeZoneName')
	return part?.value ?? ''
}

/**
 * Human-readable start time for the detail meta line, e.g. `"18:00 EDT"`, the
 * local clock time plus the timezone abbreviation derived from the IANA zone.
 *
 * This is the one time value that does NOT go through `formatDate`: `start_time`
 * is naive wall-clock with no offset, already local to the market, so applying a
 * zone conversion to it would move the very hour it states.
 */
export const formatStartTime = (
	startTime: string | undefined | null,
	timezone: string | undefined | null,
	date?: string | null
): string => {
	if (!startTime) {
		return ''
	}
	const local = composeLocalDateTime(startTime, date)
	const parsed = dayjs(local)
	if (!parsed.isValid()) {
		return startTime
	}
	const time = parsed.format('HH:mm')
	// The naive value is first pinned to the market's zone, so the abbreviation
	// is the one in force on that date there (EDT vs EST), not today's.
	const tz = timezone
		? zoneAbbr(dayjs.tz(local, timezone).toISOString(), timezone)
		: ''
	return tz ? `${time} ${tz}` : time
}

/** Extracts the `HH:mm` time-of-day for the edit form's time select. */
export const timeOfDay = (startTime: string | undefined | null): string => {
	if (!startTime) {
		return ''
	}
	const parsed = dayjs(startTime)
	return parsed.isValid() ? parsed.format('HH:mm') : startTime
}

/**
 * True when the event's searchable fields contain the (lowercased) query: name,
 * campaign hashtag and market. Wider than the backend's `search` param, which
 * matches name and hashtag only.
 */
export const eventMatchesQuery = (
	event: MarqueeEventListItem,
	query: string
): boolean =>
	// The custom location's name as well as the market's: a Freeport event is
	// found by "Freeport" and by "Southern Maine".
	[event.name, event.hashtag, event.market.name, event.location?.name]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()
		.includes(query)

// ─── Event place (market or custom location) ─────────────────────────────────

/**
 * Where an event is held, as the event form's picker offers it: an Airbnb
 * market, or a custom location inside one (OPSSTD-10100). Both sit in one
 * list, and nothing promises their ids never collide (market ids are numbers,
 * location ids ULIDs today), so each option value carries its kind.
 */
export type EventPlace =
	| { kind: 'market'; id: string }
	| { kind: 'location'; id: string }

const MARKET_PLACE_PREFIX = 'geo:'
const LOCATION_PLACE_PREFIX = 'loc:'

/** The picker value for a place: `geo:<market id>` or `loc:<location id>`. */
export const eventPlaceValue = (place: EventPlace): string =>
	`${place.kind === 'market' ? MARKET_PLACE_PREFIX : LOCATION_PLACE_PREFIX}${place.id}`

/** The place behind a picker value, or `undefined` for one this did not make. */
export const parseEventPlace = (value: string): EventPlace | undefined => {
	if (value.startsWith(MARKET_PLACE_PREFIX)) {
		return { kind: 'market', id: value.slice(MARKET_PLACE_PREFIX.length) }
	}
	if (value.startsWith(LOCATION_PLACE_PREFIX)) {
		return {
			kind: 'location',
			id: value.slice(LOCATION_PLACE_PREFIX.length)
		}
	}
	return undefined
}

/** A custom location in the picker: "Freeport, ME · Southern Maine". */
export const locationPlaceLabel = (name: string, marketName: string): string =>
	`${name} · ${marketName}`

/**
 * The place name an event shows: its custom location when it has one (a
 * deactivated one included), its market otherwise.
 */
export const eventPlaceName = (
	event: Pick<MarqueeEventListItem, 'market' | 'location'>
): string => event.location?.name ?? event.market.name

// ─── Attendee search ──────────────────────────────────────────────────────────

/** True when the attendee's searchable fields contain the (lowercased) query. */
export const attendeeMatchesQuery = (
	attendee: MarqueeAttendee,
	query: string
): boolean => {
	const haystack = [
		attendee.name,
		attendee.email,
		attendee.company,
		attendee.segment,
		attendee.sf_child_account_id,
		attendee.sf_parent_account_id,
		attendee.airbnb_host_id
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()
	return haystack.includes(query)
}

/**
 * Filters the roster by free-text search. Returns the list unchanged for an
 * empty query.
 */
export const filterAttendees = (
	rows: MarqueeAttendee[],
	rawQuery: string
): MarqueeAttendee[] => {
	const query = rawQuery.trim().toLowerCase()
	if (!query) {
		return rows
	}
	return rows.filter((row) => attendeeMatchesQuery(row, query))
}

// ─── Export preview rows ──────────────────────────────────────────────────────

/**
 * Display label for an attendee's derived resolution status, as the assign-host
 * dialog states it back after a write.
 *
 * `ready` reads "Exportable" rather than "Ready" on purpose: it is shown next to
 * an account id, answering "will this row reach Salesforce", not "is this row
 * resolved". The other four all mean the row stays out, and each names the gap
 * it has, because the fix differs per gap.
 *
 * `missing_account` deliberately says "not found" and never "has no account":
 * the backend stamps it both when the host truly has no Salesforce account and
 * when the resolver could not answer, and those read the same on the wire. See
 * [[isExportRowUnresolved]] for the row-level lane.
 */
export const attendeeStatusToLabelKey = {
	ready: 'marquee:attendee_status_exportable',
	tagged_at_parent: 'marquee:attendee_status_tagged_at_parent',
	missing_account: 'marquee:attendee_status_missing_account',
	unresolved: 'marquee:attendee_status_unresolved',
	missing_host_id: 'marquee:attendee_status_missing_host_id'
} as const satisfies Record<MarqueeAttendeeStatus, TKey>

/**
 * The operator's terminal "give up on this row" verdict. Excluded from the CSV
 * but kept in the event record for audit, so it stops blocking the run.
 *
 * The preview reports it twice, on purpose: `disposition = 'excluded'` is the
 * lane, `is_unresolvable` the flag that drives it. They are written together
 * from the same mark, so the lane alone decides it here.
 *
 * Not to be confused with `last_export.excluded` in the same payload: that is the
 * previous run's present walk-ins with no host ID, the still-fixable case.
 */
export const isExportRowUnresolvable = (row: MarqueeExportRow): boolean =>
	row.disposition === 'excluded'

/**
 * A row still needing account resolution: walk-in (pending) or invitee
 * (recoverable-skipped). These block the run until resolved or given up on.
 *
 * `skipped` is the recoverable lane and no longer carries the terminal rows, and
 * a marked row is emitted with `is_pending_walk_in` false, so the two sets are
 * already disjoint. The unresolvable check stays first anyway: it is what keeps
 * a terminal row out of the blocking set, which is the direction that matters.
 */
export const isExportRowUnresolved = (row: MarqueeExportRow): boolean =>
	!isExportRowUnresolvable(row) &&
	(row.disposition === 'skipped' || (row.is_pending_walk_in ?? false))

/**
 * The one fix a stuck export row is offered, chosen from the row's own gap.
 *
 * - `assign_host`: no host id, or a host id whose account did not resolve. Both
 *   take the search, the first to find an id and the second to correct one.
 * - `retag_child`: the account exists but is the parent's. Searching a host is
 *   useless there; the row needs the child account fetched and stored.
 * - `none`: the row is fine as a host and is held up by something this dialog
 *   cannot touch (today, a check-in facilitator with no Salesforce user id).
 */
export type ExportRowFix = 'assign_host' | 'retag_child' | 'none'

const statusToFix = {
	missing_host_id: 'assign_host',
	unresolved: 'assign_host',
	missing_account: 'assign_host',
	tagged_at_parent: 'retag_child',
	ready: 'none'
} as const satisfies Record<MarqueeAttendeeStatus, ExportRowFix>

/**
 * Which fix [[isExportRowUnresolved]] rows offer. Reads the row's `status` when
 * the backend sends it; without it only the host id is known, and a row that has
 * one is left alone rather than sent to search a host it may already have right.
 * That fallback is the whole behaviour until the field ships, so deleting it
 * silently turns three lanes into one.
 */
export const exportRowFix = (row: MarqueeExportRow): ExportRowFix => {
	if (row.status) {
		return statusToFix[row.status]
	}
	return row.host_id ? 'none' : 'assign_host'
}

/**
 * A row held out of the run by its check-in facilitator rather than by anything
 * about the host: resolved to a child account, still skipped. Named separately
 * from the fix because it changes what the row *says*, not just what it offers.
 */
export const isExportRowMissingOwner = (row: MarqueeExportRow): boolean =>
	row.status === 'ready' && isExportRowUnresolved(row)

const statusToSkipReasonKey = {
	missing_host_id: 'marquee:export_skip_missing_host_id',
	unresolved: 'marquee:export_skip_missing_account',
	missing_account: 'marquee:export_skip_missing_account',
	tagged_at_parent: 'marquee:export_skip_tagged_at_parent',
	ready: 'marquee:export_skip_missing_owner'
} as const satisfies Record<MarqueeAttendeeStatus, TKey>

/**
 * Why a row is held out, in our own words.
 *
 * The payload carries a `skip_reason` sentence the backend composes, and it is
 * the one string in this app that used to be printed as it arrived: English,
 * untranslatable, and written to a different style guide. It also collapses
 * parent-tagged and no-account into the same text. So the row's own status is
 * read instead, and `skip_reason` is left as the machine detail it is.
 *
 * Without a status, the same two-lane fallback as [[exportRowFix]]: a row with
 * no host id is stated as such, and any other stuck row is described by the gap
 * that produced it in all but one case (the facilitator one, which the status
 * field is what makes tellable).
 */
export const exportRowSkipReasonKey = (row: MarqueeExportRow): TKey =>
	row.status
		? statusToSkipReasonKey[row.status]
		: (statusToSkipReasonKey[
				row.host_id ? 'missing_account' : 'missing_host_id'
			] as TKey)

/**
 * `attendee` arrives as a single "Name / Company" identity; the grid renders the
 * company on its own line (per design), so it is split for display.
 */
export const splitExportAttendee = (attendee?: string | null) => {
	const [name, ...companyParts] = (attendee ?? '').split(' / ')
	return {
		name: name || '-',
		company: companyParts.length ? companyParts.join(' / ') : undefined
	}
}

/**
 * Whether a preview row belongs to a walk-in. The preview carries no
 * attendee-type field: the "Name / Walk-in" identity string is the only signal,
 * so a resolved walk-in is recognised by its company slot. Drives the
 * "Resolved & ready" pill and the resolution banner's name list; a mismatch
 * degrades to the plain "Ready" pill, never to a wrong action.
 */
export const isExportRowWalkIn = (row: MarqueeExportRow): boolean =>
	splitExportAttendee(row.attendee).company === 'Walk-in'

/**
 * The CTAs the run will drop from the Call Topics cell, named for the
 * operator. `counts.unmapped_call_topics` is the backend's list of stored codes
 * with no `Call_Topic__c` mapping in Salesforce: non-blocking by contract (the
 * run exports the remaining topics), so it is an info point, never a gate.
 *
 * The list carries codes; the labels come from the preview rows, which carry
 * every CTA in its resolved `{code, label}` shape. A code no row names any
 * more (the preview counts across skipped rows too) falls back to the code
 * itself, so the list stays complete rather than silently shorter.
 */
export const unmappedExportCtaLabels = (
	preview: MarqueeExportPreview | undefined
): string[] => {
	const codes = preview?.counts?.unmapped_call_topics ?? []
	if (codes.length === 0) {
		return []
	}
	const labels = new Map<string, string>()
	for (const row of preview?.rows ?? []) {
		for (const cta of row.ctas ?? []) {
			if (cta.label) {
				labels.set(cta.code, cta.label)
			}
		}
	}
	return codes.map((code) => labels.get(code) ?? code)
}

// ─── Attendee detail ──────────────────────────────────────────────────────────

/**
 * Presentation-only tidy-up for the free-text `language` cell: a bare 2–3 letter
 * code is upper-cased (`en` → `EN`), anything longer is left exactly as the
 * invitee file wrote it.
 *
 * The field is stored verbatim with no validation, so both spellings are in the
 * data: rosters carry short codes, while the contract's own examples are
 * `"English"` / `"Spanish"`. Upper-casing unconditionally would turn those into
 * `ENGLISH`, and `Brazilian Portuguese` into a shout. The length cut is what
 * separates a code from a word; trimmed first so a stray space in the CSV cell
 * does not defeat it, which also maps an all-whitespace cell to `null`.
 */
const LANGUAGE_CODE = /^[a-z]{2,3}$/i

export const formatAttendeeLanguage = (
	value?: string | null
): string | null => {
	const trimmed = value?.trim()
	if (!trimmed) {
		return null
	}
	return LANGUAGE_CODE.test(trimmed) ? trimmed.toUpperCase() : trimmed
}

// ─── Host lookup table ────────────────────────────────────────────────────────

/**
 * How the status panel should read the snapshot's provenance (FR56).
 *
 * Three states, not two, and the middle one is the whole reason this exists.
 * `GET /resolution-source` reports the **uploaded** table only, so the list
 * bundled with the deploy comes back as a real `row_count` with null
 * provenance. Treating that as "never uploaded" would tell an operator their
 * table is missing while several hundred rows are loaded and resolving.
 *
 * - `empty` — nothing is loaded, so nothing resolves.
 * - `seeded` — the table shipped with the release and no operator has replaced
 *   it yet. Active, just not anybody's upload.
 * - `uploaded` — an operator replaced it, and the panel can name them.
 */
export type HostLookupTableState = 'empty' | 'seeded' | 'uploaded'

export const hostLookupTableState = (
	status: MarqueeResolutionSourceStatus
): HostLookupTableState => {
	if (status.row_count === 0) {
		return 'empty'
	}
	return status.uploaded_at ? 'uploaded' : 'seeded'
}

/**
 * The three reasons the upload counts rows out, in the order the response
 * declares them, paired with the sentence that says what to fix.
 *
 * Every data row in the file is either written or counted under exactly one of
 * these, so the panel that renders them accounts for the whole file rather than
 * reporting a bare "some rows failed".
 */
export const uploadRejectionToLabelKey = {
	rejected_missing_host_id: 'marquee:host_lookup_rejected_missing_host_id',
	rejected_bad_account_prefix:
		'marquee:host_lookup_rejected_bad_account_prefix',
	rejected_duplicate_host_id: 'marquee:host_lookup_rejected_duplicate_host_id'
} as const satisfies Record<string, TKey>

/** The rejection counters on the upload result, as their own keys. */
export type UploadRejectionReason = keyof typeof uploadRejectionToLabelKey

export const UPLOAD_REJECTION_REASONS = [
	'rejected_missing_host_id',
	'rejected_bad_account_prefix',
	'rejected_duplicate_host_id'
] as const satisfies readonly UploadRejectionReason[]

/** Rows the upload refused, across all three reasons. */
export const rejectedRowCount = (
	result: MarqueeResolutionSourceUploadResult
): number =>
	UPLOAD_REJECTION_REASONS.reduce(
		(total, reason) => total + result[reason],
		0
	)

/**
 * Whether the upload left the table exactly as it found it: the no-op the
 * download-edit-upload loop produces when nothing was actually edited.
 *
 * Rejections count as a change even though the stored rows may be identical,
 * because a file that lost rows on the way in is not the file the operator
 * meant to upload and saying "no changes" would bury that.
 */
export const isUnchangedUpload = (
	result: MarqueeResolutionSourceUploadResult
): boolean =>
	result.rows_added === 0 &&
	result.rows_removed === 0 &&
	rejectedRowCount(result) === 0

/**
 * The backend's own sentence for a refused upload, or `undefined` when the
 * failure carries none (a network drop, a 403 from a stale role).
 *
 * Surfaced verbatim, the exception the SDK rules carve out for backend
 * messages that carry detail: the 400 names the headers it actually saw, or
 * the rejection counts that left nothing to write, and no local key can carry
 * detail the client never had.
 */
export const apiErrorMessage = (error: unknown): string | undefined => {
	const body = errorBody(error)
	return isApiError(body) ? body.message : undefined
}

/** Human-readable file size, for a picked file the operator is about to send. */
export const formatBytes = (bytes: number): string => {
	if (bytes < 1024) {
		return `${bytes} B`
	}
	const units = ['KB', 'MB', 'GB']
	let size = bytes / 1024
	let unitIndex = 0
	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024
		unitIndex += 1
	}
	return `${size.toFixed(1)} ${units[unitIndex]}`
}

/**
 * Data rows in a CSV the operator picked, counted locally so the confirmation
 * can say what the file holds before anything is sent.
 *
 * A real record scan rather than a line count: `account_name` is a company name
 * and the download quotes the ones with commas, so a quoted newline inside a
 * field would otherwise be counted as an extra row. Quotes toggle, a doubled
 * `""` stays inside the field, and CR, LF and CRLF all end a record.
 *
 * It counts, it does not validate. The upload is the only judge of whether the
 * file is acceptable, and duplicating its header and account-prefix rules here
 * would be a second copy that drifts.
 */
export const countCsvDataRows = (text: string): number => {
	let records = 0
	let fieldStarted = false
	let inQuotes = false

	for (let index = 0; index < text.length; index += 1) {
		const char = text[index]

		if (inQuotes) {
			if (char === '"') {
				// A doubled quote is an escaped quote, still inside the field.
				if (text[index + 1] === '"') {
					index += 1
				} else {
					inQuotes = false
				}
			}
			continue
		}

		if (char === '"') {
			inQuotes = true
			fieldStarted = true
			continue
		}

		if (char === '\n' || char === '\r') {
			// Blank lines separate nothing; only a record with content counts.
			if (fieldStarted) {
				records += 1
				fieldStarted = false
			}
			// Consume the LF of a CRLF pair so it does not end a second record.
			if (char === '\r' && text[index + 1] === '\n') {
				index += 1
			}
			continue
		}

		fieldStarted = true
	}

	// A file whose last record has no trailing newline still holds that record.
	if (fieldStarted) {
		records += 1
	}

	// The first record is the header row, which is not data.
	return Math.max(records - 1, 0)
}

// ─── Resolution worklist ──────────────────────────────────────────────────────

/**
 * The lookup table's own header, in its own order, so a filled worklist row
 * pastes straight into the downloaded table. `account_name` is prefilled with
 * the attendee's name as a hint for the operator; the table wants the
 * Salesforce account name there, and the operator overwrites it while filling
 * in the account IDs.
 */
export const RESOLUTION_WORKLIST_COLUMNS = [
	'account_name',
	'airbnb_host_id',
	'sf_child_account_id',
	'sf_parent_account_id'
] as const

/**
 * The rows a resolve re-run (`POST /events/{id}/resolve`) can still fix, which
 * is what the worklist hands the operator as work to do. Three things keep a
 * row out, each because the re-run would never touch it:
 *
 * - it already holds an account: `status` is only `unresolved` or
 *   `missing_account` once both account IDs are empty, so the two name exactly
 *   the rows still waiting on the lookup table;
 * - it has **no host ID**: no join key, so no table row can ever match it (that
 *   is `missing_host_id`, which the status test above already leaves out, and
 *   the explicit check keeps a blank string out too);
 * - its host ID was **assigned by hand**: the re-run never overwrites a
 *   `manual` row, by design.
 *
 * Rows the operator has marked unresolvable are out as well: they are a verdict,
 * not a gap, and handing them back as work undoes the mark.
 *
 * Walk-ins stay in when they carry a host ID: the re-run reaches them like any
 * imported row. Note this is a different set from [[hasNoSalesforceAccount]],
 * which counts what will not export (host ID or not) and leaves walk-ins out.
 */
export const resolutionWorklistRows = (
	rows: MarqueeAttendee[]
): MarqueeAttendee[] =>
	rows.filter(
		(row) =>
			(row.status === 'unresolved' || row.status === 'missing_account') &&
			Boolean(row.airbnb_host_id) &&
			row.host_id_source !== 'manual' &&
			!row.is_unresolvable
	)

const CSV_SPECIAL_RE = /[",\r\n]/
const CSV_QUOTE_RE = /"/g
const HASHTAG_PREFIX_RE = /^#/
const WHITESPACE_RE = /\s+/

/** Wraps a CSV cell, escaping quotes, commas and line breaks per RFC 4180. */
const csvCell = (value: string): string =>
	CSV_SPECIAL_RE.test(value)
		? `"${value.replace(CSV_QUOTE_RE, '""')}"`
		: value

/**
 * The worklist as a CSV: the lookup table's columns, one row per
 * [[resolutionWorklistRows]] entry, the two account columns left empty to fill
 * in. It is a worklist, not an upload: the global table is replace-all, so
 * these rows go **into** the downloaded table, never up on their own.
 */
export const toResolutionWorklistCsv = (rows: MarqueeAttendee[]): string => {
	const body = resolutionWorklistRows(rows).map((row) =>
		[row.name, row.airbnb_host_id ?? '', '', ''].map(csvCell).join(',')
	)
	return [RESOLUTION_WORKLIST_COLUMNS.join(','), ...body].join('\n')
}

/**
 * The worklist's filename, kept far from `resolution-source.csv` (the name the
 * lookup table downloads and uploads under) so the two files cannot be mixed
 * up in a folder. Named after the campaign hashtag like the export is.
 */
export const resolutionWorklistFilename = (
	event: Pick<MarqueeEventDetail, 'id' | 'hashtag'> | undefined
): string => {
	const stem =
		event?.hashtag?.replace(HASHTAG_PREFIX_RE, '') || event?.id || 'marquee'
	return `${stem}-account-worklist.csv`
}

// ─── Team ─────────────────────────────────────────────────────────────────────

/** i18n key for each Team status badge. */
export const teamStatusToLabelKey = {
	active: 'marquee:team_status_active',
	invited: 'marquee:team_status_invited',
	invite_expired: 'marquee:team_status_invite_expired',
	deactivated: 'marquee:team_status_deactivated'
} as const satisfies Record<MarqueeTeamStatus, TKey>

/**
 * The status a roster row shows, in the backend handoff's order. Deactivated
 * wins over a pending invite: the person cannot get in either way, and
 * resending to them is refused (409 `operator-deactivated`). A pending invite
 * splits on `activation_expires_at`, compared with the current time here, so a
 * row turns expired while the page is open without a refetch.
 * `activation_pending: null` (no live Luminate account matches) reads as
 * active with nothing to resend, the handoff's default until that is decided.
 */
export const teamStatus = (
	row: MarqueeManagedOperator,
	now = Date.now()
): MarqueeTeamStatus => {
	if (!row.active) {
		return 'deactivated'
	}
	if (row.activation_pending !== true) {
		return 'active'
	}
	const expiresAt = row.activation_expires_at
		? Date.parse(row.activation_expires_at)
		: Number.NaN
	return expiresAt < now ? 'invite_expired' : 'invited'
}

/** An invite nobody has accepted yet, expired or not: what a resend is for. */
export const isPendingInvite = (row: MarqueeManagedOperator): boolean => {
	const status = teamStatus(row)
	return status === 'invited' || status === 'invite_expired'
}

/**
 * The market-manager facet of the Team filter bar. Its own vocabulary rather
 * than a boolean, because the bar's facets are all single-selects with an "all"
 * value, and the URL carries it as a string.
 */
export type TeamMarketManagerFilter = 'all' | 'yes' | 'no'

export interface TeamFilters {
	query: string
	role: MarqueeOperatorRole | 'all'
	status: MarqueeTeamStatus | 'all'
	marketManager: TeamMarketManagerFilter
}

/**
 * True when the row passes every facet of the Team filter bar. The search
 * covers what the field's placeholder promises: name, email and SF user ID.
 */
export const teamRowMatchesFilters = (
	row: MarqueeManagedOperator,
	{ query, role, status, marketManager }: TeamFilters
): boolean => {
	if (role !== 'all' && row.role !== role) {
		return false
	}
	if (status !== 'all' && teamStatus(row) !== status) {
		return false
	}
	if (
		marketManager !== 'all' &&
		row.is_market_manager !== (marketManager === 'yes')
	) {
		return false
	}
	const needle = query.trim().toLowerCase()
	if (!needle) {
		return true
	}
	return [row.name, row.email, row.sf_user_id]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()
		.includes(needle)
}

/** The row-menu items the Team table knows how to render. */
export type TeamRowAction =
	| 'edit'
	| 'resend_invite'
	| 'deactivate'
	| 'reactivate'

/**
 * Which row-menu items a roster row shows, answered on both axes at once: the
 * row's status, and the viewer's permission for the write (FR51), the same way
 * `visibleSetupActions` combines them for the attendee roster.
 *
 * An **admin row** gets nothing, whoever is looking: admins are granted and
 * changed by developers only and the API refuses every write on them (403
 * `admin-protected`), the viewer's own row included. This is the one place the
 * row's own role is read; the viewer's abilities still come from `permitted`.
 */
export const teamRowActions = (
	row: MarqueeManagedOperator,
	viewer: MarqueeOperator | undefined
): TeamRowAction[] => {
	if (row.role === 'admin') {
		return []
	}
	const status = teamStatus(row)
	const actions: TeamRowAction[] = []
	if (isMarketManagerAllowed(viewer, 'updateOperator')) {
		actions.push('edit')
	}
	if (
		isPendingInvite(row) &&
		isMarketManagerAllowed(viewer, 'resendOperatorInvitation')
	) {
		actions.push('resend_invite')
	}
	if (
		status === 'active' &&
		isMarketManagerAllowed(viewer, 'deactivateOperator')
	) {
		actions.push('deactivate')
	}
	if (
		status === 'deactivated' &&
		isMarketManagerAllowed(viewer, 'reactivateOperator')
	) {
		actions.push('reactivate')
	}
	return actions
}

/**
 * The roster carries one `name`, the writes take `first_name` and `last_name`
 * (sent together). The edit form splits on the first space, so "Maria de la
 * Cruz" opens as "Maria" / "de la Cruz", which is how the backend joined it.
 */
export const splitOperatorName = (
	name: string | null
): { firstName: string; lastName: string } => {
	const trimmed = name?.trim() ?? ''
	const space = trimmed.indexOf(' ')
	return space === -1
		? { firstName: trimmed, lastName: '' }
		: {
				firstName: trimmed.slice(0, space),
				lastName: trimmed.slice(space + 1).trim()
			}
}

/**
 * The sentence for each code the operator admin API answers with (OPSSTD-9962).
 * The client words them because the backend's `message` is English composed
 * server-side; `validation-error` is not here, its field messages are shown
 * on the fields instead.
 */
const operatorErrorToLabelKey = {
	'admin-role-required': 'marquee:team_error_admin_role_required',
	'admin-protected': 'marquee:team_error_admin_protected',
	'operator-not-found': 'marquee:team_error_operator_not_found',
	'operator-exists': 'marquee:team_error_operator_exists',
	'operator-deactivated': 'marquee:team_error_operator_deactivated',
	'operator-record-removed': 'marquee:team_error_operator_record_removed',
	'reviewer-removed': 'marquee:team_error_reviewer_removed',
	'reviewer-ambiguous': 'marquee:team_error_reviewer_ambiguous',
	'market-manager-profile-removed':
		'marquee:team_error_market_manager_profile_removed',
	'invitation-not-resendable': 'marquee:team_error_invitation_not_resendable',
	'domain-not-allowed': 'marquee:team_error_domain_not_allowed'
} as const satisfies Record<string, TKey>

export type OperatorErrorCode = keyof typeof operatorErrorToLabelKey

const isOperatorErrorCode = (code: string): code is OperatorErrorCode =>
	code in operatorErrorToLabelKey

/** The known operator-API error code on a failed call, if it carries one. */
export const operatorErrorCode = (
	error: unknown
): OperatorErrorCode | undefined => {
	const body = errorBody(error)
	return isApiError(body) && isOperatorErrorCode(body.code)
		? body.code
		: undefined
}

/** The sentence for a known operator-API error code. */
export const operatorErrorLabelKey = (code: OperatorErrorCode): TKey =>
	operatorErrorToLabelKey[code]

/**
 * The field messages of a 422 `validation-error`, keyed by the request's own
 * field names (`email`, `first_name`, `sf_user_id`…), or `undefined` for any
 * other failure.
 */
export const operatorValidationErrors = (
	error: unknown
): Record<string, string[]> | undefined => {
	const body = errorBody(error)
	return isValidationError(body) ? body.errors : undefined
}

/**
 * The first word of the person's name, for copy that addresses them ("Send a
 * new invite to Thomás?"). Falls back to the email when the roster has no
 * name.
 */
export const teamFirstName = (row: MarqueeManagedOperator): string =>
	row.name?.trim().split(WHITESPACE_RE)[0] || row.email

// ─── Locations ────────────────────────────────────────────────────────────────

/** i18n key for each Locations status badge. */
export const locationStatusToLabelKey = {
	active: 'marquee:locations_status_active',
	deactivated: 'marquee:locations_status_deactivated'
} as const satisfies Record<MarqueeLocationStatus, TKey>

export const locationStatus = (row: MarqueeLocation): MarqueeLocationStatus =>
	row.active ? 'active' : 'deactivated'

export interface LocationFilters {
	query: string
	status: MarqueeLocationStatus | 'all'
}

/**
 * True when the row passes the Locations filter bar. The search covers what
 * the field's placeholder promises: the location's name and its parent market.
 */
export const locationRowMatchesFilters = (
	row: MarqueeLocation,
	{ query, status }: LocationFilters
): boolean => {
	if (status !== 'all' && locationStatus(row) !== status) {
		return false
	}
	const needle = query.trim().toLowerCase()
	if (!needle) {
		return true
	}
	return `${row.name} ${row.market.name}`.toLowerCase().includes(needle)
}

/**
 * Active rows first, deactivated ones after (OPSSTD-10100), each group in the
 * order the API returned it. `sort` is stable, so nothing else moves.
 */
export const sortLocationsActiveFirst = (
	rows: MarqueeLocation[]
): MarqueeLocation[] =>
	[...rows].sort((a, b) => Number(b.active) - Number(a.active))

/** The row-menu items the Locations table knows how to render. */
export type LocationRowAction =
	| 'edit'
	| 'view_events'
	| 'deactivate'
	| 'reactivate'

/**
 * Which row-menu items a location shows: the writes by the viewer's
 * `permitted` (never by role), and by the row's status for the toggle.
 * "View events" is a plain read, offered whenever the location has any.
 */
export const locationRowActions = (
	row: MarqueeLocation,
	viewer: MarqueeOperator | undefined
): LocationRowAction[] => {
	const actions: LocationRowAction[] = []
	if (isMarketManagerAllowed(viewer, 'updateLocation')) {
		actions.push('edit')
	}
	if (row.event_count > 0) {
		actions.push('view_events')
	}
	if (row.active && isMarketManagerAllowed(viewer, 'deactivateLocation')) {
		actions.push('deactivate')
	}
	if (!row.active && isMarketManagerAllowed(viewer, 'reactivateLocation')) {
		actions.push('reactivate')
	}
	return actions
}

/**
 * The zone's current offset as the design shows it ("GMT-4"), formatted by the
 * browser from the IANA name. Display only: nothing converts a time with it.
 * Falls back to the name itself for a zone this browser does not know.
 */
export const timeZoneOffsetLabel = (timeZone: string): string => {
	try {
		return (
			new Intl.DateTimeFormat('en-US', {
				timeZone,
				timeZoneName: 'shortOffset'
			})
				.formatToParts(new Date())
				.find((part) => part.type === 'timeZoneName')?.value ?? timeZone
		)
	} catch {
		return timeZone
	}
}

const NON_REGION_ZONE_RE = /^(UTC|Etc\/)/

/**
 * The IANA zones the time zone picker offers, from the browser (OPSSTD-10100):
 * no endpoint lists them. The backend canonicalises the name with the same ICU
 * call and refuses `UTC` and `Etc/…`, so those are dropped here; Chromium's
 * list carries neither, other engines may.
 */
export const supportedTimeZones = (): string[] =>
	Intl.supportedValuesOf('timeZone').filter(
		(zone) => !NON_REGION_ZONE_RE.test(zone)
	)

/**
 * The sentence for each code the locations API answers with (OPSSTD-10100).
 * `validation-error` is not here: its field messages go on the fields.
 */
const locationErrorToLabelKey = {
	'admin-role-required': 'marquee:locations_error_admin_role_required',
	'location-name-taken': 'marquee:locations_error_name_taken',
	'location-in-use': 'marquee:locations_error_in_use',
	'location-not-found': 'marquee:locations_error_not_found',
	'location-market-mismatch': 'marquee:locations_error_market_mismatch'
} as const satisfies Record<string, TKey>

export type LocationErrorCode = keyof typeof locationErrorToLabelKey

const isLocationErrorCode = (code: string): code is LocationErrorCode =>
	code in locationErrorToLabelKey

/** The known locations-API error code on a failed call, if it carries one. */
export const locationErrorCode = (
	error: unknown
): LocationErrorCode | undefined => {
	const body = errorBody(error)
	return isApiError(body) && isLocationErrorCode(body.code)
		? body.code
		: undefined
}

/** The sentence for a known locations-API error code. */
export const locationErrorLabelKey = (code: LocationErrorCode): TKey =>
	locationErrorToLabelKey[code]
/**
 * A failed operator write: the sentence for its code (OPSSTD-9962), 403s
 * included, since the server is the authority even when the UI hid the
 * control. Anything unknown falls back to the generic error toast.
 */
export const handleOperatorError = (error: unknown) => {
	const code = operatorErrorCode(error)
	if (code) {
		toast.error(i18next.t(operatorErrorLabelKey(code)))
		return
	}
	errorHandler(error)
}
